import React, { useState, useRef, useCallback, useEffect } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';
import { api } from '../../lib/api';
import { useOrg } from '../../hooks/useOrg';
import { X, Plus, Trash2, ZoomIn, ZoomOut, Maximize2 } from 'lucide-react';

pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

// Palette of field types the author can drop onto the PDF.
// `checkbox` is a standalone boolean; `multiple` and `radio` are group
// fields with an options[] array — each option has its own PDF hit-area,
// matching DocuSeal's data model.
const FIELD_TYPES = [
  { type: 'text',      label: 'Text',      icon: 'Tt', color: '#EF4444' },
  { type: 'signature', label: 'Signature', icon: 'Sg', color: '#223368' },
  { type: 'initials',  label: 'Initials',  icon: 'AA', color: '#EC4899' },
  { type: 'date',      label: 'Date',      icon: 'D',  color: '#F59E0B' },
  { type: 'checkbox',  label: 'Checkbox',  icon: '✓',  color: '#10B981' },
  { type: 'multiple',  label: 'Multiple',  icon: '☑',  color: '#A855F7', isGroup: true },
  { type: 'radio',     label: 'Radio',     icon: '◉',  color: '#06B6D4', isGroup: true },
];

const GROUP_DEFAULT_SIZE = { width: 2.2, height: 2.2 };

const BASE_PDF_WIDTH = 650;
const MIN_ZOOM = 0.5;
const MAX_ZOOM = 3;
const ZOOM_STEP = 0.25;

// Unique-ish id factory (fine for client-side placements; server rewrites on save)
const newId = (prefix = 'f') => `${prefix}_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 6)}`;

export default function FieldPlacementEditor({ template, onSave, onClose }) {
  const { currentOrg } = useOrg();
  const [numPages, setNumPages] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [fields, setFields] = useState(() => migrateLegacyPlacements(template.field_placements || []));
  const [selectedField, setSelectedField] = useState(null);
  // placementMode is either { kind: 'new', ft } — placing a brand-new field,
  // or { kind: 'option', fieldId } — adding another option to an existing
  // multiple/radio field. Click on the PDF resolves it.
  const [placementMode, setPlacementMode] = useState(null);
  const [saving, setSaving] = useState(false);
  const [zoom, setZoom] = useState(1);
  const pageRef = useRef(null);
  const pdfScrollRef = useRef(null);
  const pdfWidth = Math.round(BASE_PDF_WIDTH * zoom);

  const zoomIn = () => setZoom(z => Math.min(MAX_ZOOM, +(z + ZOOM_STEP).toFixed(2)));
  const zoomOut = () => setZoom(z => Math.max(MIN_ZOOM, +(z - ZOOM_STEP).toFixed(2)));
  const zoomReset = () => setZoom(1);

  // Click on PDF to add a new field or a new option to an existing group
  const handlePageClick = useCallback((e) => {
    if (!placementMode) {
      setSelectedField(null);
      return;
    }
    const rect = pageRef.current?.getBoundingClientRect();
    if (!rect) return;
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;

    if (placementMode.kind === 'option') {
      // Add a new option to an existing group field
      const fieldId = placementMode.fieldId;
      setFields(prev => prev.map(f => {
        if (f.id !== fieldId) return f;
        const nextIndex = (f.options || []).length + 1;
        return {
          ...f,
          options: [
            ...(f.options || []),
            {
              id: newId('opt'),
              label: `Option ${nextIndex}`,
              page: currentPage,
              x: Math.max(0, Math.min(x - 1, 98 - GROUP_DEFAULT_SIZE.width)),
              y: Math.max(0, Math.min(y - 1, 98 - GROUP_DEFAULT_SIZE.height)),
              width: GROUP_DEFAULT_SIZE.width,
              height: GROUP_DEFAULT_SIZE.height,
            },
          ],
        };
      }));
      setPlacementMode(null);
      return;
    }

    // placementMode.kind === 'new'
    const ft = placementMode.ft;
    const baseId = newId('f');

    if (ft.isGroup) {
      // Create a group field with its first option placed where clicked
      const field = {
        id: baseId,
        page: currentPage,
        type: ft.type,
        label: ft.type === 'radio' ? 'Question' : 'Select all that apply',
        required: true,
        options: [
          {
            id: newId('opt'),
            label: 'Option 1',
            page: currentPage,
            x: Math.max(0, Math.min(x - 1, 98 - GROUP_DEFAULT_SIZE.width)),
            y: Math.max(0, Math.min(y - 1, 98 - GROUP_DEFAULT_SIZE.height)),
            width: GROUP_DEFAULT_SIZE.width,
            height: GROUP_DEFAULT_SIZE.height,
          },
        ],
      };
      setFields(prev => [...prev, field]);
      setSelectedField(field.id);
      setPlacementMode(null);
      return;
    }

    // Regular single-placement field
    const field = {
      id: baseId,
      page: currentPage,
      x: Math.max(0, Math.min(x - 8, 80)),
      y: Math.max(0, Math.min(y - 2, 92)),
      width: ft.type === 'signature' ? 25 : ft.type === 'checkbox' ? 2.2 : 18,
      height: ft.type === 'signature' ? 8 : ft.type === 'checkbox' ? 2.2 : 4,
      type: ft.type,
      label: ft.label,
      required: true,
      auto_fill: ft.type === 'date' ? 'appointment_date' : null,
    };
    setFields(prev => [...prev, field]);
    setSelectedField(field.id);
    setPlacementMode(null);
  }, [placementMode, currentPage]);

  // Drag a single-placement field OR a specific option hit-area
  const startDrag = (e, target) => {
    e.stopPropagation();
    e.preventDefault();
    const rect = pageRef.current?.getBoundingClientRect();
    if (!rect) return;

    // target: { fieldId, optionId? }
    const { fieldId, optionId } = target;
    setSelectedField(fieldId);
    const field = fields.find(f => f.id === fieldId);
    if (!field) return;
    const source = optionId
      ? (field.options || []).find(o => o.id === optionId)
      : field;
    if (!source) return;

    const startX = e.clientX;
    const startY = e.clientY;
    const origX = source.x;
    const origY = source.y;

    const onMove = (me) => {
      const dx = ((me.clientX - startX) / rect.width) * 100;
      const dy = ((me.clientY - startY) / rect.height) * 100;
      setFields(prev => prev.map(f => {
        if (f.id !== fieldId) return f;
        if (optionId) {
          return {
            ...f,
            options: f.options.map(o => o.id === optionId
              ? { ...o, x: Math.max(0, Math.min(origX + dx, 100 - o.width)), y: Math.max(0, Math.min(origY + dy, 100 - o.height)) }
              : o),
          };
        }
        return { ...f, x: Math.max(0, Math.min(origX + dx, 100 - f.width)), y: Math.max(0, Math.min(origY + dy, 100 - f.height)) };
      }));
    };

    const onUp = () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
    };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  };

  // Resize a single-placement field OR a specific option hit-area
  const startResize = (e, target, corner) => {
    e.stopPropagation();
    e.preventDefault();
    const rect = pageRef.current?.getBoundingClientRect();
    if (!rect) return;
    const { fieldId, optionId } = target;
    const field = fields.find(f => f.id === fieldId);
    if (!field) return;
    const source = optionId
      ? (field.options || []).find(o => o.id === optionId)
      : field;
    if (!source) return;

    const startX = e.clientX;
    const startY = e.clientY;
    const origW = source.width;
    const origH = source.height;
    const origX = source.x;
    const origY = source.y;

    const onMove = (me) => {
      const dx = ((me.clientX - startX) / rect.width) * 100;
      const dy = ((me.clientY - startY) / rect.height) * 100;
      const next = { x: origX, y: origY, width: origW, height: origH };
      if (corner === 'br') { next.width = Math.max(1.5, origW + dx); next.height = Math.max(1.5, origH + dy); }
      if (corner === 'r')  { next.width = Math.max(1.5, origW + dx); }
      if (corner === 'b')  { next.height = Math.max(1.5, origH + dy); }
      setFields(prev => prev.map(f => {
        if (f.id !== fieldId) return f;
        if (optionId) {
          return { ...f, options: f.options.map(o => o.id === optionId ? { ...o, ...next } : o) };
        }
        return { ...f, ...next };
      }));
    };

    const onUp = () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
    };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  };

  const removeField = (fieldId) => {
    setFields(prev => prev.filter(f => f.id !== fieldId));
    if (selectedField === fieldId) setSelectedField(null);
  };

  const removeOption = (fieldId, optionId) => {
    setFields(prev => prev.map(f => f.id === fieldId
      ? { ...f, options: (f.options || []).filter(o => o.id !== optionId) }
      : f));
  };

  const updateField = (fieldId, patch) => {
    setFields(prev => prev.map(f => f.id === fieldId ? { ...f, ...patch } : f));
  };

  const updateOption = (fieldId, optionId, patch) => {
    setFields(prev => prev.map(f => f.id === fieldId
      ? { ...f, options: f.options.map(o => o.id === optionId ? { ...o, ...patch } : o) }
      : f));
  };

  const [saveError, setSaveError] = useState('');

  const handleSave = async () => {
    setSaving(true);
    setSaveError('');
    try {
      await api.updateFormTemplate(currentOrg.id, template.id, { field_placements: fields });
      onSave?.();
    } catch (err) {
      console.error('Save error:', err);
      setSaveError(err.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  // Escape + zoom shortcuts (Cmd/Ctrl +/-/0)
  useEffect(() => {
    const handler = (e) => {
      if (e.key === 'Escape') { setSelectedField(null); setPlacementMode(null); return; }
      if (!(e.metaKey || e.ctrlKey)) return;
      if (e.key === '=' || e.key === '+') { e.preventDefault(); zoomIn(); }
      else if (e.key === '-') { e.preventDefault(); zoomOut(); }
      else if (e.key === '0') { e.preventDefault(); zoomReset(); }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, []);

  // Cmd/Ctrl + scroll to zoom inside the PDF pane
  useEffect(() => {
    const el = pdfScrollRef.current;
    if (!el) return;
    const onWheel = (e) => {
      if (!(e.metaKey || e.ctrlKey)) return;
      e.preventDefault();
      if (e.deltaY < 0) zoomIn(); else zoomOut();
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, []);

  const sel = fields.find(f => f.id === selectedField);
  const isGroupField = sel && (sel.type === 'multiple' || sel.type === 'radio');

  // For rendering — collect all shapes on this page (options too)
  const pageShapes = React.useMemo(() => {
    const shapes = [];
    for (const f of fields) {
      if (f.type === 'multiple' || f.type === 'radio') {
        for (const o of (f.options || [])) {
          if (o.page === currentPage) shapes.push({ kind: 'option', field: f, option: o });
        }
      } else if (f.page === currentPage) {
        shapes.push({ kind: 'field', field: f });
      }
    }
    return shapes;
  }, [fields, currentPage]);

  const placementLabel = placementMode?.kind === 'option'
    ? 'Click to place the next option'
    : placementMode?.ft
      ? `Click to place: ${placementMode.ft.label}`
      : '';

  return (
    <div className="fixed inset-0 bg-[#1a1a2e] z-[200] flex">
      {/* Left sidebar — field types + pages */}
      <div className="w-[200px] bg-[#111827] border-r border-white/10 flex flex-col flex-shrink-0">
        <div className="p-4 border-b border-white/10">
          <h3 className="text-[14px] font-bold text-white">Fields</h3>
        </div>

        <div className="flex-1 overflow-y-auto p-3 space-y-1">
          {FIELD_TYPES.map(ft => {
            const active = placementMode?.kind === 'new' && placementMode.ft.type === ft.type;
            return (
              <button
                key={ft.type}
                onClick={() => setPlacementMode(active ? null : { kind: 'new', ft })}
                className={`w-full flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-left transition-all ${
                  active ? 'bg-white/20 ring-1 ring-white/30' : 'hover:bg-white/10'
                }`}
              >
                <span className="w-7 h-7 rounded-md flex items-center justify-center text-[11px] font-bold text-white" style={{ background: ft.color }}>{ft.icon}</span>
                <span className="text-[12px] font-medium text-white/80">{ft.label}</span>
              </button>
            );
          })}
        </div>

        {numPages > 1 && (
          <div className="p-3 border-t border-white/10 flex items-center justify-between">
            <button onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1} className="text-[11px] text-white/50 disabled:opacity-30 hover:text-white">Prev</button>
            <span className="text-[11px] text-white/70">{currentPage} / {numPages}</span>
            <button onClick={() => setCurrentPage(p => Math.min(numPages, p + 1))} disabled={currentPage === numPages} className="text-[11px] text-white/50 disabled:opacity-30 hover:text-white">Next</button>
          </div>
        )}

        <div className="p-3 border-t border-white/10 space-y-2">
          {saveError && <div className="text-[11px] text-red-400 mb-2">{saveError}</div>}
          <button onClick={handleSave} disabled={saving} className="w-full py-2.5 rounded-lg text-[13px] font-semibold text-white bg-[#0DA7CA] hover:bg-[#0891B2] transition-all disabled:opacity-50">
            {saving ? 'Saving...' : `Save (${fields.length} fields)`}
          </button>
          <button onClick={onClose} className="w-full py-2 rounded-lg text-[12px] font-medium text-white/60 hover:text-white hover:bg-white/10 transition-all">
            Cancel
          </button>
        </div>
      </div>

      {/* PDF area */}
      <div
        ref={pdfScrollRef}
        className="flex-1 overflow-auto py-6 px-4 relative"
        onClick={() => { if (!placementMode) setSelectedField(null); }}
      >
        {/* Floating zoom controls */}
        <div className="sticky top-0 z-40 flex justify-end pointer-events-none mb-2 -mt-2">
          <div className="pointer-events-auto flex items-center gap-0.5 bg-[#111827]/95 border border-white/10 rounded-lg shadow-xl backdrop-blur p-1">
            <button
              onClick={zoomOut}
              disabled={zoom <= MIN_ZOOM}
              className="w-7 h-7 flex items-center justify-center rounded text-white/70 hover:text-white hover:bg-white/10 disabled:opacity-30"
              title="Zoom out (⌘-)"
            >
              <ZoomOut size={14} />
            </button>
            <button
              onClick={zoomReset}
              className="px-2 h-7 rounded text-[11px] font-semibold text-white/70 hover:text-white hover:bg-white/10 min-w-[48px]"
              title="Reset zoom (⌘0)"
            >
              {Math.round(zoom * 100)}%
            </button>
            <button
              onClick={zoomIn}
              disabled={zoom >= MAX_ZOOM}
              className="w-7 h-7 flex items-center justify-center rounded text-white/70 hover:text-white hover:bg-white/10 disabled:opacity-30"
              title="Zoom in (⌘+)"
            >
              <ZoomIn size={14} />
            </button>
            <div className="w-px h-5 bg-white/10 mx-0.5" />
            <button
              onClick={zoomReset}
              className="w-7 h-7 flex items-center justify-center rounded text-white/70 hover:text-white hover:bg-white/10"
              title="Fit"
            >
              <Maximize2 size={13} />
            </button>
          </div>
        </div>

        <div className="flex justify-center">
          <div className="relative" ref={pageRef} onClick={handlePageClick} style={{ cursor: placementMode ? 'crosshair' : 'default' }}>
            <Document file={template.document_url} onLoadSuccess={({ numPages: n }) => setNumPages(n)}>
              <Page pageNumber={currentPage} width={pdfWidth} renderTextLayer={false} renderAnnotationLayer={false} />
            </Document>

          {pageShapes.map(shape => {
            if (shape.kind === 'field') {
              const field = shape.field;
              const ft = FIELD_TYPES.find(t => t.type === field.type);
              const isSel = selectedField === field.id;
              const color = ft?.color || '#6B7280';
              return (
                <div
                  key={field.id}
                  onMouseDown={(e) => startDrag(e, { fieldId: field.id })}
                  onClick={(e) => { e.stopPropagation(); setSelectedField(field.id); }}
                  className={`absolute select-none cursor-grab active:cursor-grabbing ${isSel ? 'z-20' : 'z-10'}`}
                  style={{
                    left: `${field.x}%`, top: `${field.y}%`,
                    width: `${field.width}%`, height: `${field.height}%`,
                    minWidth: 30, minHeight: 18,
                  }}
                >
                  <div
                    className="w-full h-full rounded-[3px] border-2 flex items-center transition-all"
                    style={{ borderColor: color, background: `${color}18` }}
                  >
                    <div className="flex items-center gap-1 px-1.5 w-full pointer-events-none">
                      <span className="text-[10px] font-bold" style={{ color }}>{ft?.icon}</span>
                      <span className="text-[9px] font-medium truncate" style={{ color }}>{field.label}</span>
                    </div>
                    {isSel && (
                      <button
                        onMouseDown={e => e.stopPropagation()}
                        onClick={(e) => { e.stopPropagation(); removeField(field.id); }}
                        className="absolute -top-2.5 -right-2.5 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center shadow-md hover:bg-red-600 z-30"
                      >
                        <X size={10} strokeWidth={3} />
                      </button>
                    )}
                  </div>
                  {isSel && (
                    <>
                      <div onMouseDown={(e) => startResize(e, { fieldId: field.id }, 'r')} className="absolute top-1/2 -right-1.5 -translate-y-1/2 w-3 h-3 bg-white border-2 rounded-full cursor-ew-resize z-30" style={{ borderColor: color }} />
                      <div onMouseDown={(e) => startResize(e, { fieldId: field.id }, 'b')} className="absolute -bottom-1.5 left-1/2 -translate-x-1/2 w-3 h-3 bg-white border-2 rounded-full cursor-ns-resize z-30" style={{ borderColor: color }} />
                      <div onMouseDown={(e) => startResize(e, { fieldId: field.id }, 'br')} className="absolute -bottom-1.5 -right-1.5 w-3 h-3 bg-white border-2 rounded-full cursor-nwse-resize z-30" style={{ borderColor: color }} />
                    </>
                  )}
                </div>
              );
            }

            // Option hit-area (part of a multiple/radio group)
            const { field, option } = shape;
            const ft = FIELD_TYPES.find(t => t.type === field.type);
            const color = ft?.color || '#A855F7';
            const isFieldSel = selectedField === field.id;
            return (
              <div
                key={option.id}
                onMouseDown={(e) => startDrag(e, { fieldId: field.id, optionId: option.id })}
                onClick={(e) => { e.stopPropagation(); setSelectedField(field.id); }}
                className={`absolute select-none cursor-grab active:cursor-grabbing ${isFieldSel ? 'z-20' : 'z-10'}`}
                style={{
                  left: `${option.x}%`, top: `${option.y}%`,
                  width: `${option.width}%`, height: `${option.height}%`,
                }}
              >
                <div
                  className="w-full h-full rounded-[3px] border-2 transition-all"
                  style={{ borderColor: color, background: `${color}22` }}
                />
                {isFieldSel && (
                  <span
                    className="absolute bottom-full left-1/2 -translate-x-1/2 mb-0.5 px-1.5 py-[1px] rounded text-[9px] font-semibold whitespace-nowrap pointer-events-none text-white shadow"
                    style={{ background: color }}
                  >
                    {option.label}
                  </span>
                )}
                {isFieldSel && (
                  <>
                    <div onMouseDown={(e) => startResize(e, { fieldId: field.id, optionId: option.id }, 'br')} className="absolute -bottom-1 -right-1 w-2.5 h-2.5 bg-white border-2 rounded-full cursor-nwse-resize z-30" style={{ borderColor: color }} />
                  </>
                )}
              </div>
            );
          })}

          {placementMode && (
            <div className="absolute top-3 left-1/2 -translate-x-1/2 bg-[#222] text-white text-[12px] font-medium px-4 py-2 rounded-full shadow-xl z-30 pointer-events-none">
              {placementLabel}
            </div>
          )}
          </div>
        </div>
      </div>

      {/* Right sidebar — properties */}
      <div className="w-[260px] bg-[#111827] border-l border-white/10 flex flex-col flex-shrink-0 overflow-y-auto">
        <div className="p-4 border-b border-white/10">
          <h3 className="text-[14px] font-bold text-white">Properties</h3>
        </div>

        {sel ? (
          <div className="p-4 space-y-4">
            <div>
              <label className="block text-[10px] font-semibold text-white/50 uppercase tracking-wider mb-1.5">
                {isGroupField ? 'Question' : 'Label'}
              </label>
              <input
                type="text"
                className="w-full px-3 py-2 bg-white/10 border border-white/10 rounded-lg text-[12px] text-white focus:outline-none focus:border-white/30"
                value={sel.label}
                onChange={e => updateField(sel.id, { label: e.target.value })}
              />
            </div>
            <div>
              <label className="block text-[10px] font-semibold text-white/50 uppercase tracking-wider mb-1.5">Type</label>
              <div className="flex items-center gap-2">
                <span className="w-6 h-6 rounded flex items-center justify-center text-[10px] font-bold text-white" style={{ background: FIELD_TYPES.find(t => t.type === sel.type)?.color }}>
                  {FIELD_TYPES.find(t => t.type === sel.type)?.icon}
                </span>
                <span className="text-[12px] text-white/70 capitalize">{FIELD_TYPES.find(t => t.type === sel.type)?.label || sel.type}</span>
              </div>
            </div>
            <label className="flex items-center gap-2.5 cursor-pointer">
              <input type="checkbox" checked={!!sel.required} onChange={e => updateField(sel.id, { required: e.target.checked })} className="accent-[#0DA7CA]" />
              <span className="text-[12px] text-white/70">Required</span>
            </label>

            {sel.type === 'date' && (
              <div className="bg-white/5 rounded-lg p-2.5">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={sel.auto_fill === 'appointment_date'} onChange={e => updateField(sel.id, { auto_fill: e.target.checked ? 'appointment_date' : null })} className="accent-[#F59E0B]" />
                  <span className="text-[11px] text-[#F59E0B]">Auto-fill appointment date</span>
                </label>
              </div>
            )}

            {isGroupField && (
              <div>
                <label className="block text-[10px] font-semibold text-white/50 uppercase tracking-wider mb-2">
                  Options ({(sel.options || []).length})
                </label>
                <div className="space-y-1.5">
                  {(sel.options || []).map((opt, i) => (
                    <div key={opt.id} className="flex items-center gap-1.5 bg-white/5 rounded-lg px-2 py-1.5">
                      <span className="text-[10px] text-white/40 font-mono w-4">{i + 1}.</span>
                      <input
                        type="text"
                        className="flex-1 min-w-0 px-2 py-1 bg-white/10 border border-white/10 rounded text-[12px] text-white focus:outline-none focus:border-white/30"
                        value={opt.label}
                        onChange={e => updateOption(sel.id, opt.id, { label: e.target.value })}
                      />
                      <button
                        onClick={() => removeOption(sel.id, opt.id)}
                        disabled={(sel.options || []).length <= 1}
                        className="w-6 h-6 flex items-center justify-center text-white/40 hover:text-red-400 disabled:opacity-30"
                        title="Remove option"
                      >
                        <Trash2 size={12} />
                      </button>
                    </div>
                  ))}
                </div>
                <button
                  onClick={() => setPlacementMode({ kind: 'option', fieldId: sel.id })}
                  className="mt-2 w-full py-2 rounded-lg text-[12px] font-semibold flex items-center justify-center gap-1.5 bg-[rgba(168,85,247,0.18)] text-[#C084FC] border border-[rgba(168,85,247,0.4)] hover:bg-[rgba(168,85,247,0.28)] transition-all"
                >
                  <Plus size={13} />
                  Add option
                </button>
                <p className="text-[10px] text-white/40 mt-2 leading-snug">
                  Each option has its own hit-area on the document. Click the button above, then click the PDF to place it.
                </p>
              </div>
            )}

            {!isGroupField && (
              <div className="pt-2 border-t border-white/10">
                <div className="grid grid-cols-2 gap-2 text-[10px] text-white/40">
                  <div>X: {sel.x?.toFixed?.(1)}%</div>
                  <div>Y: {sel.y?.toFixed?.(1)}%</div>
                  <div>W: {sel.width?.toFixed?.(1)}%</div>
                  <div>H: {sel.height?.toFixed?.(1)}%</div>
                </div>
              </div>
            )}

            <button onClick={() => removeField(sel.id)} className="w-full py-2 rounded-lg text-[11px] font-semibold text-red-400 border border-red-400/30 hover:bg-red-400/10 transition-all">
              Delete Field
            </button>
          </div>
        ) : (
          <div className="p-4 text-center text-[12px] text-white/30 mt-8">
            {placementMode ? 'Click on the document to place the field' : 'Select a field to edit its properties'}
          </div>
        )}
      </div>
    </div>
  );
}

// Migrate legacy group_id/select_mode/group_label placements into the new
// multiple/radio shape. Each former group becomes a single field with
// options[]. Safe to call on already-migrated data (no-op).
function migrateLegacyPlacements(placements) {
  const hasLegacy = placements.some(p => p.type === 'checkbox' && p.group_id);
  if (!hasLegacy) return placements;

  const byGroup = new Map();
  const migrated = [];

  for (const p of placements) {
    if (p.type === 'checkbox' && p.group_id) {
      if (!byGroup.has(p.group_id)) {
        const modeHolder = placements.find(x => x.group_id === p.group_id && x.select_mode);
        const mode = modeHolder?.select_mode === 'one' ? 'radio' : 'multiple';
        const label = modeHolder?.group_label || p.label || 'Select options';
        const groupField = { id: p.group_id, type: mode, label, required: true, page: p.page, options: [] };
        byGroup.set(p.group_id, groupField);
        migrated.push(groupField);
      }
      const group = byGroup.get(p.group_id);
      group.options.push({
        id: p.id,
        label: p.label || `Option ${group.options.length + 1}`,
        page: p.page,
        x: p.x, y: p.y, width: p.width, height: p.height,
      });
    } else {
      migrated.push(p);
    }
  }
  return migrated;
}
